/**
 * Shared Constants
 * 
 * Domain URLs, API endpoints, and other constants shared across web apps
 */

// Domain Configuration
export const DOMAINS = {
  LOCAL: {
    WEBSITE: 'http://zivohealth.localhost',
    ADMIN: 'http://admin.zivohealth.localhost',
    PORTAL: 'http://doctor.zivohealth.localhost',
    RESET: 'http://reset.zivohealth.localhost',
    API: 'http://api.zivohealth.localhost',
  },
  PRODUCTION: {
    WEBSITE: 'https://zivohealth.ai',
    ADMIN: 'https://admin.zivohealth.ai',
    PORTAL: 'https://doctor.zivohealth.ai',
    RESET: 'https://reset.zivohealth.ai',
    API: 'https://api.zivohealth.ai',
    /** WebSocket API Gateway for chat status (same as mobile: ws-api.zivohealth.ai) */
    WS_API: 'wss://ws-api.zivohealth.ai',
  },
} as const;

// API Base URLs
export const API_BASE_URLS = {
  LOCAL: `${DOMAINS.LOCAL.API}/api/v1`,
  PRODUCTION: `${DOMAINS.PRODUCTION.API}/api/v1`,
} as const;

// Get current environment
export const getCurrentEnvironment = (): 'local' | 'production' => {
  if (typeof window !== 'undefined') {
    return window.location.hostname.includes('localhost') ? 'local' : 'production';
  }
  return 'production';
};

// Get API base URL based on environment
export const getApiBaseUrl = (): string => {
  const env = getCurrentEnvironment();
  return env === 'local' ? API_BASE_URLS.LOCAL : API_BASE_URLS.PRODUCTION;
};

/**
 * Base URL for chat status WebSocket. In production use WebSocket API Gateway (ws-api.zivohealth.ai)
 * so the connection works like mobile; otherwise use the same host as the API with ws/wss.
 */
export function getChatStatusWebSocketBaseUrl(apiBaseUrl?: string): string {
  const base = (apiBaseUrl || (typeof window !== 'undefined' && (window as any).__API_BASE__) || '').trim();
  if (base.includes('api.zivohealth.ai')) {
    return DOMAINS.PRODUCTION.WS_API;
  }
  // Production portal (doctor.zivohealth.ai) should always use WS API Gateway even if env var is missing
  if (typeof window !== 'undefined' && window.location.hostname === 'doctor.zivohealth.ai') {
    return DOMAINS.PRODUCTION.WS_API;
  }
  if (!base) return '';
  return base.replace(/^http:\/\//, 'ws://').replace(/^https:\/\//, 'wss://').replace(/\/api\/v1\/?$/, '').replace(/\/$/, '') || base;
}

/**
 * Full chat status WebSocket URL for a session. Use with send-first-message when isProductionWsApi is true.
 */
export function getChatStatusWebSocketUrl(sessionId: number, apiBaseUrl?: string): { url: string; sendFirstMessage: boolean } {
  const wsBase = getChatStatusWebSocketBaseUrl(apiBaseUrl);
  const isProductionWsApi = wsBase === DOMAINS.PRODUCTION.WS_API;
  const url = isProductionWsApi
    ? `${wsBase}/?session_id=${sessionId}`
    : `${wsBase}/api/v1/chat-sessions/status?session_id=${sessionId}`;
  return { url, sendFirstMessage: isProductionWsApi };
}
